// ─────────────────────────────────────────────────────────────────────────────
// NJ Transit's buses on the map: which of body / badge / heading arrow each display mode
// draws, and which Xcelsior tier the body uses.
//
// Framework-free, like railModel / ferryModel, so it is unit-tested.
//
// ── The body is the MTA bus's own Xcelsior 40 ──
// NJ Transit's local fleet is New Flyer Xcelsior XD40s — the body `xcel40Model.ts` already
// carries — so an NJ bus is that glTF, unpatched. It does NOT wear the rider's MTA bus livery:
// that setting is about MTA buses, and an NJ Transit bus in MTA blue-and-yellow would be
// a different agency's paint. The unpatched body is the exporter's white, which is closer to
// NJ Transit's white-and-silver than any MTA scheme; an NJ Transit livery would be an entry
// in liveries.ts (with `bus` set and a way to pick it per agency), not something to fake here.
//
// ── Why NOT the MTA bus's zoom ladder outright ──
// An MTA bus draws a mesh at every zoom (its L2 is a ~320-triangle box). NJ Transit runs
// ~2,000 buses across a whole state, so the zoomed-out map would be a field of bodies too
// small to read. Below XCEL40_MESH_L1_ZOOM, under the default display, an NJ bus is its
// route badge plus a heading arrow — the railroads' rule (railRenderMode).
//
// ── Motion ──
// Buses glide along their route's drawn line between GPS fixes with the railroads' glide
// (`advanceRailGlide` in railModel.ts) — it is geometry, not railway: project the fix onto
// the nearest line of the route, glide along it, fall back to a straight line off-route.
// ─────────────────────────────────────────────────────────────────────────────

import type { VehicleDisplay } from '../../store/settings'
import { XCEL40_MESH_L1_ZOOM, xcel40LodForZoom, type Xcel40Lod } from './xcel40Model'

export interface NjBusRenderMode {
  /** The Xcelsior tier to draw, or null for no body. */
  mesh: Xcel40Lod | null
  badge: boolean
  /** The flat heading dart beside the badge — the zoomed-out stand-in for the bus. */
  arrow: boolean
}

/** What an NJ Transit bus draws under each of the rider's four display modes. */
export function njBusRenderMode(display: VehicleDisplay, zoom: number): NjBusRenderMode {
  const tier = xcel40LodForZoom(zoom)
  switch (display) {
    case 'labels':
      return { mesh: null, badge: true, arrow: false }
    case 'trains':
      return { mesh: tier, badge: false, arrow: false }
    case 'both':
      return { mesh: tier, badge: true, arrow: false }
    default:
      return zoom >= XCEL40_MESH_L1_ZOOM
        ? { mesh: tier, badge: false, arrow: false }
        : { mesh: null, badge: true, arrow: true }
  }
}

/** Badge altitude above a drawn body, in metres — clear of its roof at the 1.5× bus scale. */
export const NJ_BUS_LABEL_ALT = 8
